// Подсказка при наведении на любую кнопку — без ручной разметки каждой.
//
// Кнопок в интерфейсе больше сотни, и подпись у них бывает трёх сортов:
// написанная рядом с иконкой, спрятанная в aria-label (кнопка-иконка) и
// обрезанная многоточием, когда панель узкая. В двух последних случаях
// наведение не показывало ничего, и понять назначение кнопки было неоткуда.
//
// Расставлять `title` руками по всем зонам — правка, которая устаревает на
// следующей же новой кнопке, поэтому подпись собирается на лету при наведении.
// Авторская подсказка всегда важнее: там, где `title` уже написан (тулбар,
// снимки, рейки панелей), мы не трогаем ничего — она объясняет смысл, а не
// повторяет надпись.

/** Элементы, которые ведут себя как кнопка и по которым осмысленно всплывать. */
const TARGET_SELECTOR = 'button, summary, [role="button"], [role="tab"], a[href]';

/** Наша метка на атрибуте: чужой `title` мы не перезаписываем и не удаляем. */
const OWN_MARK = 'data-auto-title';

function collapse(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Текст вылезает за свою рамку хотя бы в одном узле кнопки.
 *
 * Многоточие ставит не сама кнопка, а вложенный span (см. карточки библиотеки
 * и шапки вариантов), поэтому проверяется и она, и её потомки. Единица допуска
 * — на дробные ширины при масштабировании страницы: без неё подсказка
 * вылезала бы на кнопках, где ничего не обрезано.
 */
function isClipped(el: Element): boolean {
  if (el.scrollWidth > el.clientWidth + 1) return true;
  for (const child of el.querySelectorAll('*')) {
    if (child.scrollWidth > child.clientWidth + 1) return true;
  }
  return false;
}

export interface TooltipInput {
  /** aria-label кнопки, если есть. */
  ariaLabel: string;
  /** видимый текст кнопки. */
  text: string;
  /** текст обрезан рамкой. */
  clipped: boolean;
}

/**
 * Что показать при наведении — или null, если показывать нечего.
 *
 * Повторить надпись, которую человек и так читает целиком, — шум. Поэтому
 * подсказка появляется в трёх случаях:
 *
 * 1. Видимой подписи нет вовсе — кнопка-иконка («Снимок», «История»).
 * 2. Подпись есть, но автор всё равно написал aria-label, и он другой. Значит
 *    видимого мало: «−» с меткой «Меньше путешественников», «…» с меткой
 *    «Меню варианта». Именно эти кнопки и непонятны без подсказки.
 * 3. Подпись обрезана рамкой — показываем целиком.
 *
 * Совпал aria-label с надписью — показывать нечего: метка просто дублирует
 * текст для чтения с экрана.
 */
export function pickTooltip({ ariaLabel, text, clipped }: TooltipInput): string | null {
  const aria = collapse(ariaLabel);
  const visible = collapse(text);
  const label = aria || visible;
  if (!label) return null;
  if (!visible) return label;
  if (aria && aria.toLowerCase() !== visible.toLowerCase()) return aria;
  return clipped ? label : null;
}

function applyTo(el: HTMLElement): void {
  // Чужой title трогать нельзя: он объясняет смысл кнопки, а не повторяет
  // надпись, и заменить его собранным текстом значило бы потерять смысл.
  const own = el.hasAttribute(OWN_MARK);
  if (el.hasAttribute('title') && !own) return;

  const tooltip = pickTooltip({
    ariaLabel: el.getAttribute('aria-label') ?? '',
    text: el.textContent ?? '',
    clipped: isClipped(el),
  });

  if (tooltip) {
    el.setAttribute('title', tooltip);
    el.setAttribute(OWN_MARK, '');
  } else if (own) {
    // Подпись перестала обрезаться (панель растянули, сменили язык) — свою
    // подсказку убираем, иначе она осталась бы висеть устаревшей.
    el.removeAttribute('title');
    el.removeAttribute(OWN_MARK);
  }
}

/**
 * Вешает один делегированный обработчик на документ.
 *
 * Подпись пересобирается на каждое наведение, а не один раз при монтировании:
 * текст кнопки меняется от языка, счётчиков и ширины панели, и запомненная
 * подсказка быстро разошлась бы с тем, что написано на кнопке.
 *
 * @returns функция снятия обработчиков
 */
export function installAutoTitles(root: Document | null = typeof document === 'undefined' ? null : document): () => void {
  if (!root) return () => {};

  function onPoint(e: Event): void {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const el = target.closest(TARGET_SELECTOR);
    if (el instanceof HTMLElement) applyTo(el);
  }

  // pointerover покрывает и мышь, и перо; focusin добавляет клавиатуру —
  // всплывающую подсказку браузер по фокусу не покажет, но атрибут к моменту
  // наведения уже будет верным.
  root.addEventListener('pointerover', onPoint, true);
  root.addEventListener('focusin', onPoint, true);
  return () => {
    root.removeEventListener('pointerover', onPoint, true);
    root.removeEventListener('focusin', onPoint, true);
  };
}
